import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { ErrorBoundary } from 'react-error-boundary';
import { Theme } from '@bizzabo/design-system';
import { AppToolId, BuildInAppToolId, IAppToolbarItem } from '../../types/appTools';
import { isBackgroundEqualToBody } from '../../utils/AppToolsBodyUtils';
import MeetingsAppTool from './AppTools/MeetingsAppTool';
import MessagesAppTool from './AppTools/MessagesAppTool';
import CollaborationAppTool from './AppTools/CollaborationAppTool';
import AdaChatTool from './AppTools/AdaChatTool';
import { AppToolbarIdConfig } from '../../config/AppToolbarIdConfig';
import { getRenderByDefault } from '../../config/AppToolbarRenderByDefault';
import AppToolsErrorMessage from '../AppToolsErrorMessage/AppToolsErrorMessage';
import { onAppToolGeneralErrorHandler } from '../../utils/ErrorHandler';
import SessionExtensionAppTool from './AppTools/SessionExtensionAppTool';
import AppExtension from '../../../extensions/components/AppExtension';

export interface IAppViewProp {
    appToolbarItems: IAppToolbarItem[];
    selectedApp: AppToolId;
    isExpanded: boolean;
    shouldShowCollapseAnimation: boolean;
    shouldShowExpandAnimation: boolean;
}

const AppView: React.FC<IAppViewProp> = ({
    appToolbarItems,
    selectedApp,
    isExpanded,
    shouldShowCollapseAnimation,
    shouldShowExpandAnimation,
}) => {
    const containerRef = useRef(null);
    const [isSameBackground, setIsSameBackground] = useState<boolean>();

    const classes = useStyles({ isSameBackground });

    useEffect(() => {
        if (containerRef.current) {
            setIsSameBackground(isBackgroundEqualToBody(containerRef.current));
        }
    }, []);

    const isInternal = !!AppToolbarIdConfig[selectedApp];

    const shouldRenderApp = appTool =>
        getRenderByDefault(appTool) || (appTool === selectedApp && isExpanded);

    return (
        <div className={classes.container} ref={containerRef}>
            {shouldRenderApp(BuildInAppToolId.SessionChats) && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <CollaborationAppTool selectedApp={selectedApp} />
                </ErrorBoundary>
            )}
            {shouldRenderApp(BuildInAppToolId.Messages) && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <MessagesAppTool selectedApp={selectedApp} />
                </ErrorBoundary>
            )}
            {shouldRenderApp(BuildInAppToolId.Meetings) && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <MeetingsAppTool selectedApp={selectedApp} />
                </ErrorBoundary>
            )}
            {shouldRenderApp(BuildInAppToolId.SupportChat) && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <AdaChatTool
                        selectedApp={selectedApp}
                        isExpanded={isExpanded}
                        shouldShowCollapseAnimation={shouldShowCollapseAnimation}
                        shouldShowExpandAnimation={shouldShowExpandAnimation}
                    />
                </ErrorBoundary>
            )}
            {!isInternal && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <SessionExtensionAppTool
                        selectedApp={selectedApp}
                        extensionId={
                            appToolbarItems.find(
                                tool => tool.id === BuildInAppToolId.SessionExtension
                            )?.extensionId
                        }
                    />
                </ErrorBoundary>
            )}
            {!isInternal && (
                <ErrorBoundary
                    FallbackComponent={AppToolsErrorMessage}
                    onError={onAppToolGeneralErrorHandler}
                >
                    <AppExtension
                        extensionId={
                            appToolbarItems.find(
                                tool => tool.id === BuildInAppToolId.EventExtension
                            )?.extensionId
                        }
                    />
                </ErrorBoundary>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme, { isSameBackground: boolean }>(
    theme => ({
        container: {
            height: '100%',
            width: '100%',
            background: 'white',
            borderRadius: '33px 0px 0px 33px',
            borderLeft: ({ isSameBackground }) => (isSameBackground ? '1px solid #D7D7D7' : 0),
            flex: 1,
            overflow: 'hidden',

            [theme.breakpoints.down('sm')]: {
                borderRadius: '33px 33px 0px 0px',
                boxShadow: '0px -4px 14px rgba(0, 0, 0, 0.1)',
                borderLeft: 0,
                borderTop: ({ isSameBackground }) => (isSameBackground ? '1px solid #D7D7D7' : 0),
            },
        },
    }),
    {
        classNamePrefix: 'AppView',
    }
);

export default AppView;
